import React, {lazy, useEffect, useRef, useCallback} from 'react'
import {
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'

import {ReactNotifications, Store} from 'react-notifications-component'

import { useMatomo } from '@jonkoops/matomo-tracker-react'

import useServiceWorker from '../../hooks/useServiceWorker'
import useBroadcastChannel from '../../hooks/useBroadcastChannel'

import Header from '../Header'

import 'react-notifications-component/dist/theme.css'
import 'animate.css/animate.min.css'

import {config} from '../../config'


/* Each of the lazy-loaded components will be loaded only when needed, receiving their own js chunk after compiling */
const Home = lazy(() => import('../Home'))
const Selector = lazy(() => import('../Selector'))
const Lectures = lazy(() => import('../Lectures'))
const Loader = lazy(() => import('../Loader'))

function App({
  loading = true,
  faculties = [],
}) {
  const location = useLocation()
  const { trackPageView } = useMatomo()

  /* --- MATOMO TRACKING --- */
  useEffect(() => {
    if (config.ENABLE_MATOMO)
      trackPageView({
        documentTitle: 'Startseite'
      })
    // eslint-disable-next-line
  }, [])

  // Using a "dark" (primary-based) theme on the selector page only
  const dark = location.pathname === config.SELECTOR
  const is_home = location.pathname === config.HOME

  /* --- BROADCAST CHANNEL NOTIFICATIONS --- */

  const presentingMessage = useRef(false)

  // Triggered whenever the installation app prompt should be displayed
  const showBroadcastMessage = useCallback((message) => {
    Store.addNotification({
      title: message.title,
      message: message.message,
      type: message.error ? 'danger' : 'success',
      insert: "top-center",
      container: "top-center",
      animationIn: ["animate__animated", "animate__slideInDown"],
      animationOut: ["animate__animated", "animate__fadeOut"],
      onRemoval: () => { presentingMessage.current = false },
      dismiss: {
        duration: 10000,
        onScreen: true
      },
    })
  }, [presentingMessage])

  const [message, showMessage] = useBroadcastChannel(showBroadcastMessage)
  useEffect(() => {
    if (!presentingMessage.current && message !== null) {
      showMessage()
      presentingMessage.current = true
    }
  }, [message, showMessage, presentingMessage])

  /* --- NEW VERSION UPDATE MESSAGE --- */

  // New app version
  const {waitingWorker, showReload, reloadPage} = useServiceWorker();

  // Handler to be called depending on user interaction
  const handleAppUpdate = useCallback(() => {
    reloadPage()
  }, [reloadPage])

  // Triggered whenever we want to show the app update message
  const showAppUpdateMessage = useCallback(() => {
    Store.addNotification({
      title: "Neue Version verfügbar",
      message: "Tippen Sie auf diese Nachricht, um die App auf die neueste Version zu aktualisieren.",
      type: "success",
      insert: "bottom-center",
      container: "bottom-center",
      animationIn: ["animate__animated", "animate__fadeIn"],
      animationOut: ["animate__animated", "animate__fadeOut"],
      onRemoval: handleAppUpdate,
      dismiss: {
        duration: 10000,
        onScreen: true
      }
    })
  }, [handleAppUpdate])

  // Decides when to show the app update message
  useEffect(() => {
    if (faculties.length > 0 && showReload && waitingWorker) {
      showAppUpdateMessage()
    }
  }, [waitingWorker, showReload, showAppUpdateMessage, faculties]);

  /* --- RENDERING THE APP --- */

  return (
    <>
      <ReactNotifications
        isMobile={true}
        breakpoint={768}
      />

      <div className={`vvz-app${dark ? ' bg-primary' : ''} vvz-${is_home ? 'home' : (location.pathname === config.SELECTOR ? 'selector' : 'lectures')}`}>
        {/* Header */}
        <Header/>

        {/* Content */}
        <Routes>
          <Route element={<Home/>} path={config.HOME}/>
          <Route element={<Selector/>} path={config.SELECTOR}/>
          <Route element={<Lectures/>} path={config.LECTURES}/>
        </Routes>
      </div>

      {
        loading && (
          <Loader dark={dark}/>
        )
      }
    </>
  )
}

App.propTypes = {
  loading: PropTypes.bool,
  faculties: PropTypes.array,
}

const mapDispatchToProps = dispatch => ({})

const mapStateToProps = state => ({
  loading: state.vvz.loading,
  faculties: state.vvz.faculties,
})

export default connect(mapStateToProps, mapDispatchToProps)(App)
